/**
 * Format a date string to Romanian format
 */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('ro-RO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format a number as Romanian currency (RON)
 */
export function formatPrice(price: number | string | null | undefined): string {
  if (price === null || price === undefined) return '0 RON';
  const amount = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(amount)) return '0 RON';
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: 'RON',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format a phone number to Romanian format
 */
export function formatPhone(phone: string): string {
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // Format as 07XX XXX XXX
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  
  return phone;
}

/**
 * Format file size in bytes to human readable format
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes === 0 || isNaN(bytes)) {
    return '0 B';
  }
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  const value = bytes / Math.pow(k, i);
  if (isNaN(value)) return '0 B';
  
  return `${Math.round(value)} ${sizes[i]}`;
}

/**
 * Format a percentage
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.0%';
  }
  
  const percentage = Math.round(value * 100);
  if (isNaN(percentage)) return '0%';
  
  return `${percentage}%`;
}

/**
 * Format an address from components
 */
export function formatAddress({
  strada,
  numar,
  bloc,
  scara,
  apartament,
  localitate,
  judet
}: {
  strada: string;
  numar: string;
  bloc?: string;
  scara?: string;
  apartament?: string;
  localitate: string;
  judet: string;
}): string {
  const parts = [
    `${strada} ${numar}`,
    bloc ? `Bl. ${bloc}` : null,
    scara ? `Sc. ${scara}` : null,
    apartament ? `Ap. ${apartament}` : null,
    localitate,
    judet
  ];
  
  return parts.filter(Boolean).join(', ');
}

/**
 * Format AWB tracking number with spaces
 */
export function formatAwb(awb: string): string {
  return awb.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
}

/**
 * Format invoice number
 */
export function formatInvoiceNumber(series: string, number: string): string {
  return `${series}/${number}`;
}
